import {
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { useWorkspaceStore } from "../../store/workspace/WorkspaceStore";
import { WorkspaceBreadcrumbs } from "../Workspace/Header/WorkspaceBreadcrumbs/WorkspaceBreadcrumbs";
import { ICPAnalysisTab } from "./components/AnalysisTab/ICPAnalysisTab";
import { ICPDefinitionTab } from "./components/DefinitionTab/ICPDefinitionTab";
import { ICPCopyBanner } from "./components/SAMSOMSection/ICPCopyBanner";
import { ICPTab } from "./types";
import { tabItems } from "./utils";

/**
 * ICP (Ideal Customer Profile) page component
 */
export const ICPPage: FC = () => {
  const [activeTab, setActiveTab] = useState<ICPTab>(ICPTab.DEFINITION);
  const saveICPToDraft = useWorkspaceStore((state) => state.saveICPToDraft);
  const saveWorkspace = useWorkspaceStore((state) => state.saveWorkspace);

  const icpSAM = useWorkspaceStore((state) => state.icpSAM);
  const icpSOM = useWorkspaceStore((state) => state.icpSOM);
  const isNewIcpAvailableForSam = useWorkspaceStore(
    (state) => state.isNewIcpAvailableForSam
  );
  const isNewIcpAvailableForSom = useWorkspaceStore(
    (state) => state.isNewIcpAvailableForSom
  );
  const isLoadingIcpData = useWorkspaceStore((state) => state.isLoadingIcpData);

  const syncTab = async (_: React.SyntheticEvent, newValue: ICPTab) => {
    // Auto-save when switching to Analysis tab
    if (newValue === ICPTab.ANALYSIS) {
      saveICPToDraft();
      // Save to backend to persist ICP changes
      try {
        await saveWorkspace();
      } catch (error) {
        console.error("Failed to save ICP data to backend:", error);
      }
    }
    setActiveTab(newValue);
  };

  const handleViewAnalysis = async () => {
    saveICPToDraft();
    // Save to backend to persist ICP changes
    try {
      await saveWorkspace();
    } catch (error) {
      console.error("Failed to save ICP data to backend:", error);
    }
    setActiveTab(ICPTab.ANALYSIS);
  };

  // Show loading state while workspace or ICP data is loading
  if (isLoadingIcpData) {
    return (
      <Stack direction="column" height="100%" flex={1}>
        <Box>
          <WorkspaceBreadcrumbs />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              marginBottom: "20px",
            }}
          >
            <Typography
              sx={{
                fontSize: "32px",
                fontWeight: 300,
              }}
            >
              Customer Profile
            </Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={syncTab}
            sx={{ borderBottom: 1, borderColor: "divider" }}
            slotProps={{
              indicator: { style: { top: 0 } },
            }}
          >
            {tabItems.map(({ id, label }, index) => (
              <Tab key={id} id={id} label={label} value={index} />
            ))}
          </Tabs>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          gap={3}
        >
          <CircularProgress />
          <Stack alignItems="center">
            <Typography variant="body1" marginLeft={2}>
              Loading Customer Profile configuration...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments.
            </Typography>
          </Stack>
        </Box>
      </Stack>
    );
  }

  return (
    <Box
      padding="30px"
      display="flex"
      flexDirection="column"
      height="100%"
      flex={1}
    >
      <Box>
        <WorkspaceBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
            marginBottom: "20px",
          }}
        >
          <Typography
            sx={{
              fontSize: "32px",
              fontWeight: 300,
            }}
          >
            Customer Profile
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={syncTab}
          sx={{ borderBottom: 1, borderColor: "divider" }}
          slotProps={{
            indicator: { style: { top: 0 } },
          }}
        >
          {tabItems.map(({ id, label }, index) => (
            <Tab key={id} id={id} label={label} value={index} />
          ))}
        </Tabs>
      </Box>

      {/* Banner positioned below tabs - only show in Analysis tab when configuration is updated */}
      {activeTab === ICPTab.ANALYSIS &&
        (isNewIcpAvailableForSam || isNewIcpAvailableForSom) && (
          <ICPCopyBanner sam={icpSAM} som={icpSOM} />
        )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {activeTab === ICPTab.DEFINITION && (
          <ICPDefinitionTab onViewAnalysis={handleViewAnalysis} />
        )}
        {activeTab === ICPTab.ANALYSIS && <ICPAnalysisTab />}
      </Box>
    </Box>
  );
};
